<template>
  <div class="app-surface">
    <NavBar />
    
    <main class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <div class="mb-6 rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-soft md:p-6">
        <div class="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <div class="mb-2 inline-flex rounded-lg bg-jackfruit-100 px-3 py-1.5 text-xs font-black text-ink-700">
              菠萝蜜书库
            </div>
            <h1 class="text-3xl font-black text-ink-900">想看什么，马上开刷</h1>
            <p class="mt-2 text-sm text-ink-500">按频道、爽点和热度筛一筛，下一本上头书就在这里。</p>
          </div>

          <div class="relative w-full md:max-w-md">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="搜索小说名、作者..."
              class="w-full rounded-xl border border-ink-100 bg-jackfruit-50/60 py-3 pl-11 pr-4 text-sm text-ink-900 outline-none transition-all placeholder:text-ink-300 focus:border-jackfruit-400 focus:bg-white focus:ring-2 focus:ring-jackfruit-200"
            />
            <div class="absolute left-4 top-3 text-ink-300">⌕</div>
          </div>
        </div>
      </div>

      <div class="mb-5 px-1">
        <div class="py-1.5">
          <div class="grid grid-cols-[3.25rem_minmax(0,1fr)] items-center gap-2 sm:grid-cols-[4rem_minmax(0,1fr)]">
            <div class="text-xs font-bold text-ink-400">频道:</div>
            <div class="grid min-w-0 grid-cols-5 gap-1 sm:flex sm:flex-wrap sm:gap-2">
              <button
                v-for="channel in channels"
                :key="channel.id"
                @click="switchCategory(channel.id)"
                class="relative rounded-full px-1.5 py-1.5 text-center text-xs font-black transition sm:px-3 sm:text-sm"
                :class="activeChannel === channel.id
                  ? 'bg-jackfruit-pop text-ink-900'
                  : 'text-ink-700 hover:bg-jackfruit-50 hover:text-ink-900'"
              >
                <span class="sm:hidden">{{ channel.title }}</span>
                <span class="hidden sm:inline">{{ channel.title }}</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="activeChannel !== 'all' && currentSubcategories.length > 0" class="py-1.5">
          <div class="grid grid-cols-[3.25rem_minmax(0,1fr)] items-center gap-2 sm:grid-cols-[4rem_minmax(0,1fr)]">
            <div class="text-xs font-bold text-ink-400">分类:</div>
            <div class="grid min-w-0 grid-cols-5 gap-1 sm:flex sm:flex-wrap sm:gap-2">
              <button
                @click="clearSubcategory"
                class="rounded-full px-1.5 py-1.5 text-xs font-black transition sm:px-3"
                :class="!activeSubcategory
                  ? 'bg-jackfruit-pop text-ink-900'
                  : 'text-ink-600 hover:bg-jackfruit-50 hover:text-ink-900'"
              >
                全部
              </button>
              <button
                v-for="sub in currentSubcategories"
                :key="sub.id"
                @click="toggleSubcategory(sub.id)"
                class="rounded-full px-1.5 py-1.5 text-xs font-black transition sm:px-3"
                :class="activeSubcategory === sub.id
                  ? 'bg-jackfruit-pop text-ink-900'
                  : 'text-ink-600 hover:bg-jackfruit-50 hover:text-ink-900'"
              >
                {{ sub.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 class="text-lg font-black text-ink-900">
          {{ activeChannelTitle }}
          <span class="ml-2 text-sm font-normal text-ink-500">共 {{ filteredBooks.length }} 本</span>
        </h2>
        <div class="flex gap-2 overflow-x-auto pb-1 text-sm scrollbar-hide">
          <button
            v-for="opt in sortOptions"
            :key="opt.value"
            @click="sortBy = opt.value"
            class="shrink-0 rounded-lg px-3 py-1.5 font-bold transition-colors"
            :class="sortBy === opt.value ? 'bg-white text-pulp-600 shadow-sm' : 'text-ink-500 hover:bg-white hover:text-ink-900'"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <!-- 加载态 -->
      <div v-if="isLoading" class="flex justify-center py-16">
        <div class="h-10 w-10 animate-spin rounded-full border-b-2 border-jackfruit-500"></div>
      </div>

      <div v-else-if="error" class="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
        {{ error }}
      </div>

      <div v-else-if="filteredBooks.length > 0" class="grid grid-cols-3 gap-2.5 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
        <BookCard
          v-for="book in filteredBooks"
          :key="book.id"
          :book="book"
        />
      </div>

      <div v-else class="text-center py-16">
        <div class="mb-4 text-5xl">📭</div>
        <p class="text-ink-500">没有找到匹配的书籍</p>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import NavBar from '@/components/NavBar.vue'
import BookCard from '@/components/BookCard.vue'
import { bookApi } from '@/api'
import type { Book } from '@/types/user'
import { SUBCATEGORIES, getSubcategoryTag } from '@/config/categories'

type ChannelId = 'all' | 'male' | 'female' | 'general' | 'cyberpunk'

const channelConfig = [
  { id: 'all' as const, title: '全部', emoji: '📚', description: '展示所有分类的小说作品' },
  { id: 'cyberpunk' as const, title: '赛博', emoji: '🤖', description: 'AI、人机关系、霓虹与未来都市' },
  { id: 'male' as const, title: '男频', emoji: '⚔️', description: '热血、玄幻、修仙、都市异能' },
  { id: 'female' as const, title: '女频', emoji: '💕', description: '言情、甜宠、穿越、宫斗宅斗' },
  { id: 'general' as const, title: '综合', emoji: '✨', description: '科幻、悬疑、历史、现实题材' }
]

const route = useRoute()
const router = useRouter()

const activeChannel = ref<ChannelId>((route.query.category as ChannelId) || 'all')
const activeSubcategory = ref<string>(route.query.sub as string || '')
const sortBy = ref<'views' | 'chapters' | 'title'>('views')
const searchQuery = ref((route.query.search as string) || '')
const books = ref<Book[]>([])
const isLoading = ref(false)
const error = ref('')

// 监听 URL 参数变化 → 重新加载
watch(() => route.query, (q) => {
  const newChannel = (q.category as ChannelId) || 'all'
  const newSub = q.sub as string || ''
  if (newChannel !== activeChannel.value || newSub !== activeSubcategory.value) {
    activeChannel.value = newChannel
    activeSubcategory.value = newSub
    searchQuery.value = (q.search as string) || ''
    loadBooks()
  }
})

const sortOptions = [
  { value: 'views' as const, label: '🔥 热度' },
  { value: 'chapters' as const, label: '📄 章节' },
  { value: 'title' as const, label: '📖 书名' }
]

const normalizeBook = (book: Book): Book => ({
  ...book,
  description: book.description || '',
  tags: parseTags(book.tags),
  coverUrl: book.coverUrl || ''
})

const loadBooks = async () => {
  isLoading.value = true
  error.value = ''
  try {
    const loadedBooks: Book[] = []
    let page = 1
    let pages = 1
    const subcategoryTag = activeSubcategory.value ? getSubcategoryTag(activeSubcategory.value) : undefined

    do {
      const response = await bookApi.getBooks({
        page,
        limit: 100,
        status: 'published',
        category: activeChannel.value !== 'all' ? activeChannel.value : undefined,
        subcategory: subcategoryTag,
        search: searchQuery.value.trim() || undefined
      })

      if (response.code !== 200 || !response.data?.items) {
        throw new Error(response.message || '书籍加载失败')
      }

      loadedBooks.push(...response.data.items)
      pages = response.data.pagination?.pages || page
      page += 1
    } while (page <= pages)

    books.value = loadedBooks.map(normalizeBook)
  } catch (err: any) {
    error.value = err?.message || '书籍加载失败，请确认后端服务已启动'
  } finally {
    isLoading.value = false
  }
}

const currentSubcategories = computed(() =>
  activeChannel.value === 'all'
    ? []
    : SUBCATEGORIES.filter(s => s.category === activeChannel.value)
)

const activeSubLabel = computed(() => {
  if (!activeSubcategory.value) return ''
  const sub = SUBCATEGORIES.find(s => s.id === activeSubcategory.value)
  return sub ? `${sub.emoji} ${sub.label}` : activeSubcategory.value
})

const parseTags = (tags: any): string[] => {
  if (Array.isArray(tags)) return tags as string[]
  if (typeof tags !== 'string') return []
  try {
    const parsed = JSON.parse(tags)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return tags.split(',').map((t: string) => t.trim()).filter(Boolean)
  }
}

const channels = computed(() =>
  channelConfig.map(ch => ({
    ...ch,
    count: ch.id === 'all' ? books.value.length : books.value.filter(b => b.category === ch.id).length
  }))
)

const activeChannelTitle = computed(() => {
  const ch = channels.value.find(c => c.id === activeChannel.value)
  const base = `${ch?.emoji || ''} ${ch?.title || ''}`
  return activeSubLabel.value ? `${base} · ${activeSubLabel.value}` : base
})

// 搜索结果和排序（客户端过滤，API 已有 category/subcategory 过滤）
const filteredBooks = computed(() => {
  let list = [...books.value]

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase()
    list = list.filter(b =>
      b.title.toLowerCase().includes(q) ||
      (b.author && b.author.toLowerCase().includes(q))
    )
  }

  // 排序
  switch (sortBy.value) {
    case 'views':
      list.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
      break
    case 'chapters':
      list.sort((a, b) => (b.totalChapters || 0) - (a.totalChapters || 0))
      break
    case 'title':
      list.sort((a, b) => a.title.localeCompare(b.title, 'zh-CN'))
      break
  }

  return list
})

const switchCategory = (id: ChannelId) => {
  router.push({ query: { sub: undefined, category: id !== 'all' ? id : undefined } })
}

const clearSubcategory = () => {
  router.push({ query: { category: activeChannel.value !== 'all' ? activeChannel.value : undefined } })
}

const toggleSubcategory = (subId: string) => {
  const newSub = activeSubcategory.value === subId ? '' : subId
  const query: Record<string, string> = {}
  if (activeChannel.value !== 'all') query.category = activeChannel.value
  if (newSub) query.sub = newSub
  router.push({ query })
}

onMounted(loadBooks)
</script>
